// Adapted from T3 Code's packages/shared/src/htmlRender.ts, commit 0ae1091d019484d474fce0fe0a89195a82134041
// (MIT, Copyright 2026 T3 Tools Inc.). Full notice: public/third-party-licenses.txt.

/**
 * Agent pages are self-contained HTML documents an agent shows in a /code
 * conversation with the `show_page` session tool (`session-tools/show-page.ts`).
 * The page lives in the call's input; the browser shows it in a sandboxed frame
 * (`agent-page-frame.tsx`) with a small bootstrap injected into its head and
 * hands it cubic's theme as CSS custom properties. No Node imports: the
 * browser reads this.
 */

import { getRecord } from '@/lib/utils/unknown';

export const AGENT_PAGE_MIN_HEIGHT = 80;
export const AGENT_PAGE_MAX_HEIGHT = 2000;
export const AGENT_PAGE_MAX_TITLE_LENGTH = 200;
/** Characters; keeps the conversation, which carries every page to the browser and to the model's later requests, light. */
export const AGENT_PAGE_MAX_HTML_LENGTH = 100_000;
/** The reply column's width on desktop, which the agent checks its page at. */
export const AGENT_PAGE_COLUMN_WIDTH = 720;

export type AgentPage = { html: string; title: string; height: number };

export type AgentPageAppearance = 'light' | 'dark';

export type AgentPageTheme = {
  appearance: AgentPageAppearance;
  variables: Readonly<Record<string, string>>;
};

export function clampAgentPageHeight(height: number): number {
  return Math.min(AGENT_PAGE_MAX_HEIGHT, Math.max(AGENT_PAGE_MIN_HEIGHT, Math.round(height)));
}

type AppearanceValues = Readonly<Record<AgentPageAppearance, string>>;

const same = (value: string): AppearanceValues => ({ light: value, dark: value });

const FOREGROUND: AppearanceValues = { light: 'hsl(240 10% 3.9%)', dark: 'hsl(0 0% 98%)' };
// A card stands out from the dark thread instead of sinking below it.
const CARD: AppearanceValues = { light: 'hsl(0 0% 100%)', dark: '#18181b' };
const ACCENT: AppearanceValues = { light: '#7c6bc4', dark: '#ccc1f0' };

/**
 * cubic's look as the variables pages style against. Names follow T3's, except
 * `--accent`, which is cubic's lavender here rather than the app's neutral hover
 * surface. The neutrals and charts are `src/styles/global.css`'s, the dark
 * background is the conversation's (`--code-chat-background`), and a status
 * color's `-foreground` is its text on `--background`. A frame cannot load
 * cubic's fonts, so pages get the system's.
 */
const PAGE_VARIABLES: Readonly<Record<string, AppearanceValues>> = {
  '--background': { light: 'hsl(0 0% 100%)', dark: 'hsl(240 5.9% 6.7%)' },
  '--foreground': FOREGROUND,
  '--muted': { light: 'hsl(240 4.8% 95.9%)', dark: 'hsl(240 3.23% 12.16%)' },
  '--muted-foreground': { light: 'hsl(240 3.8% 46.1%)', dark: 'hsl(240 5% 64.9%)' },
  '--card': CARD,
  '--card-foreground': FOREGROUND,
  '--popover': CARD,
  '--popover-foreground': FOREGROUND,
  '--secondary': { light: 'hsl(240 4.8% 95.9%)', dark: 'hsl(240 3.7% 15.9%)' },
  '--secondary-foreground': { light: 'hsl(240 5.9% 10%)', dark: 'hsl(0 0% 98%)' },
  '--border': { light: 'hsl(240 5.9% 90%)', dark: 'hsl(240 3.03% 12.94%)' },
  '--input': { light: 'hsl(240 5.9% 90%)', dark: 'hsl(240 3.7% 15.9%)' },
  '--ring': { light: 'hsl(240 10% 3.9%)', dark: 'hsl(240 4.9% 83.9%)' },
  '--primary': { light: 'hsl(240 5.9% 10%)', dark: 'hsl(0 0% 98%)' },
  '--primary-foreground': { light: 'hsl(0 0% 98%)', dark: 'hsl(240 5.9% 10%)' },
  '--accent': ACCENT,
  '--accent-foreground': { light: '#ffffff', dark: '#18181b' },
  '--accent-surface': { light: '#f1eefb', dark: '#24212f' },
  '--accent-surface-foreground': { light: '#4c3f8f', dark: '#ccc1f0' },
  '--destructive': same('#ef4444'),
  '--destructive-foreground': { light: '#b91c1c', dark: '#f87171' },
  '--destructive-surface': { light: '#fef2f2', dark: '#2a1215' },
  '--warning': same('#f59e0b'),
  '--warning-foreground': { light: '#b45309', dark: '#fbbf24' },
  '--warning-surface': { light: '#fffbeb', dark: '#2a1f0b' },
  '--success': same('#10b981'),
  '--success-foreground': { light: '#047857', dark: '#34d399' },
  '--info': same('#3b82f6'),
  '--info-foreground': { light: '#1d4ed8', dark: '#60a5fa' },
  '--code-background': { light: '#f4f4f5', dark: '#18181b' },
  '--code-foreground': { light: '#18181b', dark: '#e4e4e7' },
  '--chart-1': { light: 'hsl(12 76% 61%)', dark: 'hsl(220 70% 50%)' },
  '--chart-2': same('hsl(156 60% 51%)'),
  '--chart-3': { light: 'hsl(197 37% 24%)', dark: 'hsl(30 80% 55%)' },
  '--chart-4': { light: 'hsl(43 74% 66%)', dark: 'hsl(280 65% 60%)' },
  '--chart-5': { light: 'hsl(27 87% 67%)', dark: 'hsl(340 75% 55%)' },
  '--chart-6': ACCENT,
  '--radius': same('0.5rem'),
  '--font-sans': same('-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif'),
  // Concrete names only: some engines alias `ui-monospace` to the proportional UI font.
  '--font-mono': same('"SF Mono", "SFMono-Regular", Menlo, Consolas, "Liberation Mono", monospace'),
};

function pageTheme(appearance: AgentPageAppearance): AgentPageTheme {
  return {
    appearance,
    variables: Object.fromEntries(
      Object.entries(PAGE_VARIABLES).map(([name, values]) => [name, values[appearance]]),
    ),
  };
}

export const AGENT_PAGE_THEMES: Readonly<Record<AgentPageAppearance, AgentPageTheme>> = {
  light: pageTheme('light'),
  dark: pageTheme('dark'),
};

// The bridge between a page and the browser sends MCP Apps messages (JSON-RPC
// over postMessage), all of them notifications, since neither side waits on a
// reply: https://github.com/modelcontextprotocol/ext-apps
const HOST_CONTEXT_CHANGED_METHOD = 'ui/notifications/host-context-changed';
const OPEN_LINK_METHOD = 'ui/open-link';
const SIZE_CHANGED_METHOD = 'ui/notifications/size-changed';
// Not part of MCP Apps: the frame keeps the keyboard once the reader clicks in it, so the page
// tells cubic the reader pressed Escape, which closes a full-screen page.
const ESCAPE_PRESSED_METHOD = 'cubic/notifications/escape-pressed';

export function readAgentPageContentHeight(data: unknown): number | undefined {
  const message = getRecord(data);
  if (message?.jsonrpc !== '2.0' || message.method !== SIZE_CHANGED_METHOD) {
    return undefined;
  }
  const height = getRecord(message.params)?.height;
  return typeof height === 'number' && Number.isFinite(height) && height > 0 ? height : undefined;
}

export function isAgentPageEscapePressed(data: unknown): boolean {
  const message = getRecord(data);
  return message?.jsonrpc === '2.0' && message.method === ESCAPE_PRESSED_METHOD;
}

/** The URL a page's `ui/open-link` notification asks to open, if `data` is one with an http(s) URL. */
export function readAgentPageLink(data: unknown): string | undefined {
  const message = getRecord(data);
  if (message?.jsonrpc !== '2.0' || message.method !== OPEN_LINK_METHOD) {
    return undefined;
  }
  const url = getRecord(message.params)?.url;
  return typeof url === 'string' && /^https?:\/\//i.test(url) ? url : undefined;
}

export function agentPageThemeMessage(theme: AgentPageTheme) {
  return {
    jsonrpc: '2.0',
    method: HOST_CONTEXT_CHANGED_METHOD,
    params: { theme: theme.appearance, styles: { variables: theme.variables } },
  } as const;
}

// The frame scrolls a page taller than itself, but a scrollbar inside the
// reply reads as a box within the thread, so it stays hidden.
const BASE_CSS =
  'html{background:var(--background);color:var(--foreground);font-family:var(--font-sans);font-size:14px;line-height:1.5;-webkit-font-smoothing:antialiased;-webkit-text-size-adjust:100%;scrollbar-width:none}' +
  'html::-webkit-scrollbar{display:none}body{margin:0}code,kbd,pre,samp{font-family:var(--font-mono)}' +
  // Focus outlines paint inside the element, so the frame edge never clips them.
  ':where(:focus-visible){outline:2px solid var(--ring)}:focus-visible{outline-width:2px!important;outline-offset:-2px!important}';

function rootRule(theme: AgentPageTheme): string {
  const declarations = Object.entries(theme.variables)
    .map(([name, value]) => `${name}:${value};`)
    .join('');
  return `:root{color-scheme:${theme.appearance};${declarations}}`;
}

/**
 * Both appearances, dark unless the system prefers light, and the base
 * stylesheet: what the show-page skill's preview links, since the agent checks
 * its page without the bootstrap.
 */
export function agentPagePreviewCss(): string {
  return [
    rootRule(AGENT_PAGE_THEMES.dark),
    `@media (prefers-color-scheme: light){${rootRule(AGENT_PAGE_THEMES.light)}}`,
    BASE_CSS,
  ].join('\n');
}

// Runs synchronously in <head>, before the page's own styles and body, so the
// first paint is already themed. It rewrites its own <style> element rather
// than setting inline properties, so a page's later `:root` rules still win,
// and takes a new theme only from the page around it. A link the reader clicks
// never replaces the page inside the thread: the page asks its parent to open
// it. A `srcdoc` document resolves relative URLs against cubic's page, so a
// link to `#id` scrolls to its target here instead, and any other link that is
// not http(s) goes nowhere. The page also reports its content height, so its
// parent can fit the frame to the page, and the Escape key, so its parent can
// close a full-screen page while the frame has the keyboard.
const BOOTSTRAP_SCRIPT = [
  `(function(){var s=document.getElementById("cubic-theme"),h,o;if(!s)return;`,
  // The theme: rewrites the style element with each theme the page around it posts.
  `var b=${JSON.stringify(BASE_CSS)};function a(t){if(!t||typeof t!=="object"||!t.variables||typeof t.variables!=="object")return;var c=":root{color-scheme:"+(t.appearance==="light"?"light":"dark")+";";for(var k in t.variables){if(/^--[a-z0-9-]+$/.test(k))c+=k+":"+String(t.variables[k]).replace(/[;{}<>]/g,"")+";";}s.textContent=c+"}"+b;}window.addEventListener("message",function(e){var d=e.data,p=d&&d.params;if(e.source===window.parent&&d&&d.jsonrpc==="2.0"&&d.method===${JSON.stringify(HOST_CONTEXT_CHANGED_METHOD)}&&p&&p.styles)a({appearance:p.theme,variables:p.styles.variables});});`,
  // Links: `#id` scrolls here, http(s) links go to the parent, in a notification rather than MCP Apps'
  // request, since nothing here reads a reply, and every other link goes nowhere.
  `document.addEventListener("click",function(e){var l=e.isTrusted?e.composedPath().find(function(t){return t&&t.matches&&t.matches("a[href]");}):null,r,u,x;if(!l)return;r=l.getAttribute("href")||"";if(r===""||r.charAt(0)==="#"){e.preventDefault();try{x=document.getElementById(decodeURIComponent(r.slice(1)));}catch(y){}if(x)x.scrollIntoView();return;}e.preventDefault();if(!/^[a-z][a-z0-9+.-]*:/i.test(r))return;try{u=new URL(r);}catch(y){return;}if(!/^https?:$/.test(u.protocol))return;window.parent.postMessage({jsonrpc:"2.0",method:${JSON.stringify(OPEN_LINK_METHOD)},params:{url:u.href}},"*");},true);`,
  // Escape: tells the parent the reader pressed it, since the parent no longer sees keys typed in this frame.
  `document.addEventListener("keydown",function(e){if(e.isTrusted&&e.key==="Escape"&&!e.isComposing)window.parent.postMessage({jsonrpc:"2.0",method:${JSON.stringify(ESCAPE_PRESSED_METHOD)}},"*");});`,
  // Height: reports the content height whenever it changes.
  `var z=function(){var r=document.documentElement,v=Math.ceil(r.scrollHeight>r.clientHeight?r.scrollHeight:r.getBoundingClientRect().height);if(v===h)return;h=v;window.parent.postMessage({jsonrpc:"2.0",method:${JSON.stringify(SIZE_CHANGED_METHOD)},params:{height:v}},"*");};if(window.ResizeObserver){o=new ResizeObserver(z);o.observe(document.documentElement);}document.addEventListener("DOMContentLoaded",function(){if(o&&document.body)o.observe(document.body);z();});window.addEventListener("load",z);`,
  `})();`,
].join('');

/**
 * The page's own Content Security Policy, which narrows cubic's (a `srcdoc` frame inherits it)
 * to what the tool description promises. The show-page skill's preview uses the same policy;
 * `file:` lets that preview link its theme from disk, which a page in cubic cannot reach.
 */
export const AGENT_PAGE_CONTENT_SECURITY_POLICY = [
  "script-src 'unsafe-inline' https://unpkg.com",
  "style-src 'unsafe-inline' file: https://fonts.googleapis.com",
  'font-src data: https://fonts.gstatic.com',
  'img-src data: blob: https: http:',
  "connect-src 'none'",
  "form-action 'none'",
].join('; ');

/**
 * Puts a doctype, the page's policy, `<style id="cubic-theme">` with `theme`'s
 * `:root` rule and the base stylesheet, and the bootstrap script ahead of the
 * page, so the page's own styles and scripts come after them. The parser starts
 * the head with these, then ignores the page's own doctype and `<head>` tag and
 * adds its `<html>` attributes to the root. cubic's doctype is the one that
 * counts, so every page renders in standards mode. A `srcdoc` has no bytes to
 * decode and a frame ignores a viewport tag, so the charset and viewport tags
 * T3 adds are left out.
 */
export function injectAgentPageBootstrap(html: string, theme: AgentPageTheme): string {
  return `<!doctype html><meta http-equiv="Content-Security-Policy" content="${AGENT_PAGE_CONTENT_SECURITY_POLICY}"><style id="cubic-theme">${rootRule(theme)}${BASE_CSS}</style><script>${BOOTSTRAP_SCRIPT}</script>${html}`;
}
